import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { AdminProfile } from "@api-gen/admin_pb";
import {
  consoleLoginRealm,
  consoleStorageKey,
  detectConsoleDomain,
  type ConsoleDomain,
} from "@/console-domain";

export type { ConsoleDomain } from "@/console-domain";

function consoleParentDomain(hostname = window.location.hostname) {
  const labels = hostname.trim().toLowerCase().split(".");
  return labels.length >= 3 ? labels.slice(1).join(".") : "m6k.cn";
}

const parentDomain = consoleParentDomain();

const domainConfig = {
  root: {
    host: `root.${parentDomain}`,
    loginRealm: consoleLoginRealm("root"),
    label: "集团根控制台",
    accent: "#8d78ff",
  },
  admin: {
    host: `ops.${parentDomain}`,
    loginRealm: consoleLoginRealm("admin"),
    label: "运营控制台",
    accent: "#3fe29a",
  },
  finance: {
    host: `finance.${parentDomain}`,
    loginRealm: consoleLoginRealm("finance"),
    label: "财务控制台",
    accent: "#ffbd59",
  },
  creator: {
    host: `creator.${parentDomain}`,
    loginRealm: consoleLoginRealm("creator"),
    label: "创作者中心",
    accent: "#52a8ff",
  },
} as const;

export const useSessionStore = defineStore("admin-session", () => {
  const domain = ref<ConsoleDomain>(detectConsoleDomain());
  const profile = ref<AdminProfile>();
  const authenticated = computed(() =>
    Boolean(sessionStorage.getItem(consoleStorageKey("accessToken"))),
  );
  const currentDomain = computed(() => domainConfig[domain.value]);
  const permissions = computed(() => new Set(profile.value?.permissions ?? []));

  function has(permission: string) {
    return permissions.value.has("*") || permissions.value.has(permission);
  }
  function setSession(token: string, admin: AdminProfile) {
    sessionStorage.setItem(consoleStorageKey("accessToken"), token);
    sessionStorage.setItem(
      consoleStorageKey("profile"),
      JSON.stringify({
        adminId: admin.adminId,
        username: admin.username,
        displayName: admin.displayName,
        role: admin.role,
        permissions: admin.permissions,
        domainScope: admin.domainScope,
      }),
    );
    profile.value = admin;
  }
  function restore() {
    const saved = sessionStorage.getItem(consoleStorageKey("profile"));
    if (!saved) return;
    try {
      const restored = JSON.parse(saved) as AdminProfile;
      if (
        restored.domainScope?.trim().toLowerCase() !==
        currentDomain.value.loginRealm.toLowerCase()
      ) {
        clear();
        return;
      }
      profile.value = restored;
    } catch {
      clear();
    }
  }
  function clear() {
    sessionStorage.removeItem(consoleStorageKey("accessToken"));
    sessionStorage.removeItem(consoleStorageKey("profile"));
    profile.value = undefined;
  }
  return {
    domain,
    currentDomain,
    profile,
    authenticated,
    has,
    setSession,
    restore,
    clear,
  };
});
